/*
 * Copyright (C) 2018 Robert Bosch GmbH Copyright (C) 2018 Robert Bosch Manufacturing Solutions GmbH, Germany. All rights reserved.
 */

import { Component, Input, NgZone, OnDestroy, OnInit } from "@angular/core";
import { Observable, Subject, timer } from "rxjs";
import { map, share, skipWhile, takeUntil } from "rxjs/operators";
import { SidebarNavItem } from "../bci-sidebar/sidebar-nav-item.model";

import { UserInfo } from "src/app/shared/models/user-info";
import { LanguageEnum } from "@shared/enums/language.enum";
import { LanguageService } from "@core/services/language.service";
import { LangChangeEvent } from "@ngx-translate/core";
import { AppDownloadBadgeModalComponent } from "../../../../../../../src/app/modules/private-parts/components/app-download-badge-modal/app-download-badge-modal.component";
import { FeatureToggleService } from "@core/services/feature-toggle.service";
import { MatDialog } from "@angular/material/dialog";
import { assetUrl } from "@shared/helper/asset-url";

@Component({
  selector: "bci-sidebar-footer",
  templateUrl: "./bci-sidebar-footer.component.html",
  styleUrls: ["./bci-sidebar-footer.component.scss"],
  standalone: false
})
export class BciSidebarFooterComponent implements OnInit, OnDestroy {
  @Input() public sidebarFooterLinks: SidebarNavItem[];
  @Input() public showTime = false;
  @Input() userInfo: UserInfo;

  legalInfoText: string;
  titleQrCodeModal: string;
  descriptionQrCodeModal: string;

  legalOptionsVisible = false;

  appleStoreBadge = assetUrl("img/app/apple-store-badge-pt-BR.svg");
  googlePlayBadge = assetUrl("img/app/google-play-badge-pt-BR.svg");

  _timeNowSubject = new Subject<number>();
  timeNow: Observable<number> = this._timeNowSubject.pipe(share());

  private componentDestroyed$: Subject<any> = new Subject();
  restrictItemsOnMenu$: Observable<boolean>;
  constructor(
    private ngZone: NgZone,
    private dialog: MatDialog,
    private languageService: LanguageService,
    private featureToggleService: FeatureToggleService
  ) {}

  ngOnInit(): void {
    this.legalInfoText = "legal-info";
    this.languageService.onLangChange().subscribe((data: LangChangeEvent) => {
      const legalInfoTexts = this.getLegalInfoTexts(data.lang as LanguageEnum);
      this.legalInfoText = `${legalInfoTexts}`;

      const qrCodeTexts = this.getQRCodeTexts(data.lang as LanguageEnum);
      this.titleQrCodeModal = `${qrCodeTexts[0]}`;
      this.descriptionQrCodeModal = `${qrCodeTexts[1]}`;

      const imagePath = this.getImagePaths(data.lang as LanguageEnum);
      this.appleStoreBadge = assetUrl(`img/app/${imagePath[0]}`);
      this.googlePlayBadge = assetUrl(`img/app/${imagePath[1]}`);
    });

    /*need to schedule the timer outside of ngZone to avoid infinite waiting in e2e tests, see https://github.com/angular/protractor/issues/3349*/
    this.ngZone.runOutsideAngular(() => {
      timer(0, 10000)
        .pipe(
          skipWhile(() => !this.showTime),
          takeUntil(this.componentDestroyed$)
        )
        .subscribe(() =>
          this.ngZone.run(() => {
            this._timeNowSubject.next(Date.now());
          })
        );
    });

    this.getRestrictItemsOnMenu();
  }

  private getImagePaths(language: LanguageEnum): [string, string] {
    const map = new Map<LanguageEnum, [string, string]>([
      [
        LanguageEnum.PT_BR,
        ["apple-store-badge-pt-BR.svg", "google-play-badge-pt-BR.svg"]
      ],
      [
        LanguageEnum.ES,
        ["apple-store-badge-es.svg", "google-play-badge-es.svg"]
      ],
      [
        LanguageEnum.EN_US,
        ["apple-store-badge-en-US.svg", "google-play-badge-en-US.svg"]
      ],
      [
        LanguageEnum.DE_DE,
        ["apple-store-badge-en-US.svg", "google-play-badge-en-US.svg"]
      ],
      [
        LanguageEnum.PT_PT,
        ["apple-store-badge-pt-BR.svg", "google-play-badge-pt-BR.svg"]
      ],
      [
        LanguageEnum.ES_ES,
        ["apple-store-badge-es.svg", "google-play-badge-es.svg"]
      ],
      [
        LanguageEnum.EN_EN,
        ["apple-store-badge-en-US.svg", "google-play-badge-en-US.svg"]
      ]
    ]);
    return map.get(language) || map.get(LanguageEnum.PT_BR);
  }

  private getQRCodeTexts(language: LanguageEnum): [string, string] {
    const map = new Map<LanguageEnum, [string, string]>([
      [
        LanguageEnum.PT_BR,
        ["Escaneie o Qr Code", "Baixe agora o App DriveB Fleet!"]
      ],
      [
        LanguageEnum.ES,
        ["Escanea el código QR", "Descargue ahora la aplicación DriveB Fleet."]
      ],
      [
        LanguageEnum.EN_US,
        ["Scan the QR Code", "Download the DriveB Fleet App now!"]
      ],
      [
        LanguageEnum.DE_DE,
        [
          "Scannen Sie den QR-Code",
          "Laden Sie jetzt die DriveB Fleet App herunter!"
        ]
      ],
      [
        LanguageEnum.PT_PT,
        ["Escaneie o Qr Code", "Baixe agora o App DriveB Fleet!"]
      ],
      [
        LanguageEnum.ES_ES,
        ["Escanea el código QR", "Descargue ahora la aplicación DriveB Fleet."]
      ],
      [
        LanguageEnum.EN_EN,
        ["Scan the QR Code", "Download the DriveB Fleet App now!"]
      ]
    ]);
    return map.get(language) || map.get(LanguageEnum.PT_BR);
  }

  private getLegalInfoTexts(language: LanguageEnum): string {
    const map = new Map<LanguageEnum, string>([
      [LanguageEnum.PT_BR, "Informação Legal"],
      [LanguageEnum.ES, "Información Legal"],
      [LanguageEnum.EN_US, "Legal Information"],
      [LanguageEnum.DE_DE, "Rechtliche Hinweise"],
      [LanguageEnum.PT_PT, "Informação Legal"],
      [LanguageEnum.ES_ES, "Información Legal"],
      [LanguageEnum.EN_EN, "Legal Information"]
    ]);
    return map.get(language) || map.get(LanguageEnum.PT_BR);
  }

  openAppDownloadModal(url: string): void {
    this.dialog.open(AppDownloadBadgeModalComponent, {
      data: {
        url,
        titleQrCodeModal: this.titleQrCodeModal,
        descriptionQrCodeModal: this.descriptionQrCodeModal
      },
      width: "600px"
    });
  }

  openLegalInfoGroup(): void {
    this.legalOptionsVisible = !this.legalOptionsVisible;
  }

  ngOnDestroy(): void {
    this.componentDestroyed$.next("");
    this.componentDestroyed$.complete();
  }

  showDockPrivacySettings() {
    const dockPrivacySettings = document.getElementsByTagName(
      "dock-privacy-settings"
    )[0];
    dockPrivacySettings.setAttribute("visible", "true");
    return !1;
  }

  private getRestrictItemsOnMenu() {
    this.restrictItemsOnMenu$ = this.featureToggleService
      .verifyFeatureToggle()
      .pipe(map((res) => res.restrictItemsOnMenu));
  }
}
